'use client';

import StampLogo from '@/components/ui/logo/StampLogo';
import LanguageSwitcher from '@/components/i18n/LanguageSwitcher';
import Button from '@/components/ui/button/Button';
import Image from 'next/image';
import SectionSwitcher from '@/components/ui/section-switcher/SectionSwitcher';
import styles from './Header.module.css';
import BurguerIcon from '../icons/BurguerIcon';
import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useParams } from 'next/navigation';
import { Locales } from '@/types/i18n';
import { MerchantsMobileDrawer } from './MerchantsMobileDrawer';
import { TravellersMobileDrawer } from './TravellersMobileDrawer';
import ChevronDownIcon from '../icons/ChevronDownIcon';
import ArrowRightIcon from '../icons/ArrowRightIcon';
import Link from 'next/link';
import { withMarketQuery } from '@/lib/utils/withMarketQuery';
// Import translations
import enTranslation from '@/locales/en/translation.json';
import esTranslation from '@/locales/es/translation.json';
import deTranslation from '@/locales/de/translation.json';
import itTranslation from '@/locales/it/translation.json';
import zhCNTranslation from '@/locales/zh_CN/translation.json';

// Define a type for nested translation objects
type NestedTranslation = {
  [key: string]: string | NestedTranslation;
};

// Helper function to get translation based on locale
const getTranslation = (locale: Locales, key: string): string => {
  const keys = key.split('.');
  let translationObj: NestedTranslation;

  switch (locale) {
    case 'en':
      translationObj = enTranslation;
      break;
    case 'es':
      translationObj = esTranslation;
      break;
    case 'de':
      translationObj = deTranslation;
      break;
    case 'it':
      translationObj = itTranslation;
      break;
    case 'zh-CN':
      translationObj = zhCNTranslation;
      break;
    default:
      translationObj = enTranslation;
  }

  // Navigate through the nested keys
  let result: string | NestedTranslation = translationObj;
  for (const k of keys) {
    if (result && typeof result === 'object' && k in result) {
      result = result[k];
    } else {
      return key; // Return the key if translation not found
    }
  }

  return typeof result === 'string' ? result : key;
};

export default function Header() {
  const [showMenu, setShowMenu] = useState(false);
  const [showHelpDropdown, setShowHelpDropdown] = useState(false);
  const [showMarketDropdown, setShowMarketDropdown] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState<'es' | 'it'>('es'); // Default to Spanish market
  const pathname = usePathname();
  const params = useParams();
  const locale = (params.locale as Locales) || 'en';

  // Check URL for market parameter and country cookie on component mount
  useEffect(() => {
    const url = new URL(window.location.href);
    const marketParam = url.searchParams.get('market');

    if (marketParam === 'it') {
      // URL parameter takes precedence
      setSelectedMarket('it');
    } else if (marketParam === 'es') {
      setSelectedMarket('es');
    } else {
      // If no URL parameter, check the country cookie
      const cookies = document.cookie.split(';');
      const countryCookie = cookies.find((cookie) => cookie.trim().startsWith('user-country='));
      const country = countryCookie ? countryCookie.split('=')[1].trim() : '';

      // Set market based on detected country
      if (country === 'IT') {
        setSelectedMarket('it');
      } else {
        // Default to Spanish market for all other countries (including ES)
        setSelectedMarket('es');
      }
    }
  }, []);

  // Default to merchants page unless explicitly on travelers page
  const isMerchantsPage = !pathname.includes('/travelers');
  const faqsPath = isMerchantsPage ? `/${locale}/merchants/faqs` : `/${locale}/travelers/faqs`;

  const showOptions = () => {
    setShowMenu(!showMenu);
  };

  const toggleHelpDropdown = () => {
    setShowHelpDropdown(!showHelpDropdown);
    if (showMarketDropdown) setShowMarketDropdown(false);
  };

  const toggleMarketDropdown = () => {
    setShowMarketDropdown(!showMarketDropdown);
    if (showHelpDropdown) setShowHelpDropdown(false);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setShowMenu(false);
  };

  return (
    <header className={styles.header}>
      {/* Top row */}
      <div className={styles.topRow}>
        {/* Logo section (left side) */}
        <div className={styles.logoSection}>
          <Link href={withMarketQuery(`/${locale}/merchants`)} className={styles.logoLink}>
            <StampLogo className={styles.logo} />
          </Link>
        </div>

        {/* No market selector here - moved to right section */}

        {/* Mobile burger menu button (only visible on mobile) */}
        <div className={styles.mobileMenuButton}>
          <button className={styles.burguerBtn} onClick={showOptions}>
            <BurguerIcon />
          </button>
        </div>

        {/* Right section with navigation buttons (desktop only) */}
        <div
          className={`${styles.rightSection} ${!isMerchantsPage ? styles.travelersRightSection : ''}`}
        >
          {isMerchantsPage ? (
            <>
              {/* Market selector for merchants page */}
              <div className={styles.marketSelectorContainer}>
                <div className={styles.marketSelector} onClick={toggleMarketDropdown}>
                  <span className={styles.flagIcon}>
                    {selectedMarket === 'es' ? (
                      <Image
                        src="/images/icons/spanish-flag.png"
                        alt="Spanish flag"
                        width={20}
                        height={20}
                        style={{ borderRadius: '50%' }}
                      />
                    ) : (
                      <Image
                        src="/images/icons/italian-flag.png"
                        alt="Italian flag"
                        width={20}
                        height={20}
                        style={{ borderRadius: '50%' }}
                      />
                    )}
                  </span>
                  <span className={styles.marketText}>
                    {selectedMarket === 'es'
                      ? getTranslation(locale, 'navbar.forSpanishMarket')
                      : getTranslation(locale, 'navbar.forItalianMarket')}
                  </span>
                  <ChevronDownIcon className={styles.marketDropdownArrow} />
                </div>
                {showMarketDropdown && (
                  <div className={styles.marketDropdownMenu}>
                    <div
                      className={styles.marketOption}
                      onClick={() => {
                        setSelectedMarket('es');
                        setShowMarketDropdown(false);
                        // Remove market query parameter for Spanish (default)
                        const url = new URL(window.location.href);
                        url.searchParams.delete('market');
                        window.history.pushState({}, '', url);
                      }}
                    >
                      <span className={styles.flagIcon}>
                        <Image
                          src="/images/icons/spanish-flag.png"
                          alt="Spanish flag"
                          width={20}
                          height={20}
                          style={{ borderRadius: '50%' }}
                        />
                      </span>
                      <span>{getTranslation(locale, 'navbar.spanishMarket')}</span>
                    </div>
                    <div
                      className={styles.marketOption}
                      onClick={() => {
                        setSelectedMarket('it');
                        setShowMarketDropdown(false);
                        // Add market query parameter without redirecting
                        const url = new URL(window.location.href);
                        url.searchParams.set('market', 'it');
                        window.history.pushState({}, '', url);
                      }}
                    >
                      <span className={styles.flagIcon}>
                        <Image
                          src="/images/icons/italian-flag.png"
                          alt="Italian flag"
                          width={20}
                          height={20}
                          style={{ borderRadius: '50%' }}
                        />
                      </span>
                      <span>{getTranslation(locale, 'navbar.italianMarket')}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.merchantsNavButtonsContainer}>
                {/* Language selector */}
                <div className={styles.languageSelector}>
                  <LanguageSwitcher />
                </div>
                {/* Merchants navigation buttons */}
                <div className={styles.merchantsNavButtons}>
                  <Button
                    variant="ghost"
                    href="https://merchants.stampit.co/"
                    target="_blank"
                    as="a"
                  >
                    {getTranslation(locale, 'navbar.login')}
                  </Button>
                  <Button
                    variant="outline"
                    href={
                      selectedMarket == 'it'
                        ? 'https://sales.askforstamp.com/it/contact'
                        : 'https://sales.askforstamp.com/contact'
                    }
                    target="_blank"
                    as="a"
                  >
                    {getTranslation(locale, 'navbar.contactSales')}
                  </Button>
                  <Button
                    variant="primary"
                    as="button"
                    onClick={() => {
                      const url = selectedMarket === 'it' 
                        ? 'https://signup.stampit.co/' 
                        : 'https://sales.askforstamp.com/signup';
                      window.open(url, '_blank');
                    }}
                    id={selectedMarket === 'it' ? 'register_it' : 'register_es'}
                    name="Register"
                    title="Register"
                  >
                    {getTranslation(locale, 'navbar.signUp')}{' '}
                    <ArrowRightIcon className={styles.arrowRightIcon} />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={styles.helpDropdown}>
                <button onClick={toggleHelpDropdown} className={styles.helpButton}>
                  {getTranslation(locale, 'navbar.help')}
                  <ChevronDownIcon className={styles.dropdownArrow} />
                </button>
                {showHelpDropdown && (
                  <div className={styles.dropdownMenu}>
                    <Link
                      href={withMarketQuery(`/${locale}/travelers/guide`)}
                      className={styles.dropdownItem}
                    >
                      {getTranslation(locale, 'navbar.guide')}
                    </Link>
                    <Link href={withMarketQuery(faqsPath)} className={styles.dropdownItem}>
                      {getTranslation(locale, 'navbar.faqs')}
                    </Link>
                  </div>
                )}
              </div>

              <div className={styles.languageSelector}>
                <LanguageSwitcher />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bottom row with center section */}
      <div className={styles.bottomRow}>
        {/* Center section with switcher */}
        <SectionSwitcher isMerchantsPage={isMerchantsPage} onMenuClose={handleMenuClose} />
      </div>

      {/* Mobile drawer menus */}
      {showMenu && isMerchantsPage && (
        <MerchantsMobileDrawer open={showMenu} onClose={() => setShowMenu(false)} />
      )}
      {showMenu && !isMerchantsPage && (
        <TravellersMobileDrawer open={showMenu} onClose={() => setShowMenu(false)} />
      )}
    </header>
  );
}
